const clamp = (v, mn, mx) => Math.min(mx, Math.max(mn, v));

const rangeRisk = (value, lowRiskMin, optimalMin, optimalMax, highRiskMax) => {
  if (!Number.isFinite(value)) return 100;
  if (value < lowRiskMin) return 100;
  if (value > highRiskMax) return 100;
  if (value >= optimalMin && value <= optimalMax) return 0;
  if (value < optimalMin) {
    const span = optimalMin - lowRiskMin;
    return clamp(((optimalMin - value) / span) * 100, 0, 100);
  }
  const span = highRiskMax - optimalMax;
  return clamp(((value - optimalMax) / span) * 100, 0, 100);
};

const upperRisk = (value, optimalMax, riskMax) => {
  if (!Number.isFinite(value)) return 100;
  if (value <= optimalMax) return 0;
  if (value >= riskMax) return 100;
  return clamp(((value - optimalMax) / (riskMax - optimalMax)) * 100, 0, 100);
};

const PROFILES = {
  Uva: {
    FrancoArenoso: {
      hum: { lowRisk: 8, optimalMin: 14, optimalMax: 22, highRisk: 30 },
      cond: { optimalMax: 1.8, riskMax: 3.0 },
      temp: { lowRisk: 8, optimalMin: 18, optimalMax: 28, highRisk: 36 },
    },
    Franco: {
      hum: { lowRisk: 10, optimalMin: 18, optimalMax: 28, highRisk: 38 },
      cond: { optimalMax: 1.8, riskMax: 3.0 },
      temp: { lowRisk: 8, optimalMin: 18, optimalMax: 28, highRisk: 36 },
    },
    Arcilloso: {
      hum: { lowRisk: 15, optimalMin: 25, optimalMax: 38, highRisk: 50 },
      cond: { optimalMax: 1.8, riskMax: 3.0 },
      temp: { lowRisk: 8, optimalMin: 18, optimalMax: 28, highRisk: 36 },
    },
  },
  Cerezo: {
    FrancoArenoso: {
      hum: { lowRisk: 8, optimalMin: 16, optimalMax: 24, highRisk: 32 },
      cond: { optimalMax: 1.2, riskMax: 2.5 },
      temp: { lowRisk: 6, optimalMin: 14, optimalMax: 24, highRisk: 32 },
    },
    Franco: {
      hum: { lowRisk: 12, optimalMin: 20, optimalMax: 30, highRisk: 40 },
      cond: { optimalMax: 1.2, riskMax: 2.5 },
      temp: { lowRisk: 6, optimalMin: 14, optimalMax: 24, highRisk: 32 },
    },
    Arcilloso: {
      hum: { lowRisk: 18, optimalMin: 28, optimalMax: 40, highRisk: 52 },
      cond: { optimalMax: 1.2, riskMax: 2.5 },
      temp: { lowRisk: 6, optimalMin: 14, optimalMax: 24, highRisk: 32 },
    },
  },
  Arandano: {
    FrancoArenoso: {
      hum: { lowRisk: 10, optimalMin: 18, optimalMax: 26, highRisk: 34 },
      cond: { optimalMax: 0.6, riskMax: 1.5 },
      temp: { lowRisk: 5, optimalMin: 14, optimalMax: 20, highRisk: 28 },
    },
    Franco: {
      hum: { lowRisk: 14, optimalMin: 22, optimalMax: 32, highRisk: 42 },
      cond: { optimalMax: 0.6, riskMax: 1.5 },
      temp: { lowRisk: 5, optimalMin: 14, optimalMax: 20, highRisk: 28 },
    },
    Arcilloso: {
      hum: { lowRisk: 20, optimalMin: 30, optimalMax: 42, highRisk: 54 },
      cond: { optimalMax: 0.6, riskMax: 1.5 },
      temp: { lowRisk: 5, optimalMin: 14, optimalMax: 20, highRisk: 28 },
    },
  },
};

const DEFAULT_PROFILE = {
  hum: { lowRisk: 20, optimalMin: 40, optimalMax: 70, highRisk: 90 },
  cond: { optimalMax: 1.8, riskMax: 3.0 },
  temp: { lowRisk: 6, optimalMin: 14, optimalMax: 28, highRisk: 36 },
};

const SOIL_KEYS = {
  FrancoArenoso: 'FrancoArenoso',
  Franco: 'Franco',
  Arcilloso: 'Arcilloso',
};

const CROP_KEYS = {
  Uva: 'Uva',
  Cerezo: 'Cerezo',
  Arandano: 'Arandano',
};

export function getProfile(crop, soilType) {
  const c = PROFILES[crop];
  if (!c) return DEFAULT_PROFILE;
  const s = c[soilType];
  if (!s) return DEFAULT_PROFILE;
  return s;
}

export function calcHumRisk(value, profile) {
  return rangeRisk(value, profile.hum.lowRisk, profile.hum.optimalMin, profile.hum.optimalMax, profile.hum.highRisk);
}

export function calcCondRisk(value, profile) {
  return upperRisk(value, profile.cond.optimalMax, profile.cond.riskMax);
}

export function calcTempRisk(value, profile) {
  return rangeRisk(value, profile.temp.lowRisk, profile.temp.optimalMin, profile.temp.optimalMax, profile.temp.highRisk);
}

export function humStatus(value, profile) {
  const { lowRisk, optimalMin, optimalMax, highRisk } = profile.hum;
  if (!Number.isFinite(value)) return 'Sin datos';
  if (value < lowRisk) return 'Estrés hídrico severo: regar urgente';
  if (value < optimalMin) return 'Baja humedad: programar riego';
  if (value <= optimalMax) return 'Humedad óptima';
  if (value < highRisk) return 'Exceso de humedad: revisar drenaje';
  return 'Suelo saturado: riesgo de anoxia radical';
}

export function condStatus(value, profile) {
  const { optimalMax, riskMax } = profile.cond;
  if (!Number.isFinite(value)) return 'Sin datos';
  if (value <= optimalMax) return 'Disponibilidad de sales estable';
  if (value < riskMax) return 'Subiendo salinidad: ajustar dosis y lavar';
  return 'Riesgo salino alto: reducir fertilizante y lavar perfil';
}

export function tempStatus(value, profile) {
  const { lowRisk, optimalMin, optimalMax, highRisk } = profile.temp;
  if (!Number.isFinite(value)) return 'Sin datos';
  if (value < lowRisk) return 'Suelo muy frío: menor absorción radicular';
  if (value < optimalMin) return 'Suelo fresco: actividad radicular reducida';
  if (value <= optimalMax) return 'Temperatura de suelo estable';
  if (value < highRisk) return 'Suelo cálido: monitorear estrés térmico';
  return 'Suelo sobrecalentado: riesgo severo de estrés';
}

export { SOIL_KEYS, CROP_KEYS, DEFAULT_PROFILE };
